import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Save, Loader, AlertCircle, Plus, X, Play } from 'lucide-react';
import { neonQuery } from '../services/api';

// Tier 3 #7 - Security Alerts config UI.
// Admins edit thresholds + recipient list; server stores in app_secrets.security_alert_config.
// The scheduled cron (security-alerts-scheduled.js) reads this same row every 5 min.

const INPUT = { padding: '7px 12px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };
const LABEL = { fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', marginBottom: 4, display: 'block' };
const ROW = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 };
const SECTION = { background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginBottom: 16 };
const SECTION_TITLE = { fontSize: 13, fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 12 };

export default function SecurityAlertsConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [newRecipient, setNewRecipient] = useState('');
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await neonQuery('security_alerts_config_get', {});
      setConfig(data.config);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError('');
    setSavedMsg('');
    try {
      const r = await neonQuery('security_alerts_config_set', { config });
      if (r.config) setConfig(r.config);
      setSavedMsg('Saved.');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const addRecipient = () => {
    const e = newRecipient.trim();
    if (!e || !e.includes('@')) return;
    if (config.recipients.includes(e)) { setNewRecipient(''); return; }
    setConfig({ ...config, recipients: [...config.recipients, e] });
    setNewRecipient('');
  };

  const removeRecipient = (i) => {
    setConfig({ ...config, recipients: config.recipients.filter((_, idx) => idx !== i) });
  };

  const runTestScan = async () => {
    setTestRunning(true);
    setTestResult(null);
    try {
      const r = await fetch('/.netlify/functions/security-alerts-scheduled', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force: false }),
      });
      const data = await r.json();
      setTestResult(data);
    } catch (e) { setTestResult({ error: e.message }); }
    finally { setTestRunning(false); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}><Loader size={20} className="spin" /> Loading...</div>;
  if (error && !config) return <div style={{ padding: 40, color: '#c0392b' }}>{error}</div>;
  if (!config) return null;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '20px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Shield size={20} style={{ color: 'var(--navy, #00395C)' }} />
        <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy, #00395C)', margin: 0 }}>Security Alerts</h1>
      </div>
      <div style={{ fontSize: 12, color: 'var(--gray-600)', marginBottom: 16, lineHeight: 1.5 }}>
        Scheduled scan runs every 5 min against the auth_events log. Matches fire an email to the recipients below.
        Per-alert cooldown prevents spam.
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}><AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />{error}</div>}

      <div style={SECTION}>
        <div style={SECTION_TITLE}>Recipients</div>
        <div style={{ marginBottom: 8 }}>
          {config.recipients.length === 0 && <div style={{ fontSize: 11, color: 'var(--gray-500)', fontStyle: 'italic' }}>No recipients - no alerts will be sent.</div>}
          {config.recipients.map((e, i) => (
            <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: 'var(--gray-100)', borderRadius: 14, fontSize: 12, marginRight: 6, marginBottom: 4 }}>
              <span>{e}</span>
              <button onClick={() => removeRecipient(i)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--gray-500)', display: 'flex' }} title="Remove"><X size={12} /></button>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input type="email" value={newRecipient} onChange={e => setNewRecipient(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }} placeholder="add@crabplace.com" style={{ ...INPUT, flex: 1 }} />
          <button onClick={addRecipient} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 12px', background: 'var(--navy, #00395C)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}><Plus size={14} /> Add</button>
        </div>
      </div>

      <div style={SECTION}>
        <div style={SECTION_TITLE}>Login burst (failed logins from one IP)</div>
        <div style={ROW}>
          <div>
            <label style={LABEL}>Threshold (failures)</label>
            <input type="number" min="1" max="100" value={config.burst_threshold} onChange={e => setConfig({ ...config, burst_threshold: parseInt(e.target.value) || 5 })} style={INPUT} />
          </div>
          <div>
            <label style={LABEL}>Window (minutes)</label>
            <input type="number" min="1" max="120" value={config.burst_window_min} onChange={e => setConfig({ ...config, burst_window_min: parseInt(e.target.value) || 10 })} style={INPUT} />
          </div>
        </div>
        <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>Default: 5 failures in 10 min.</div>
      </div>

      <div style={SECTION}>
        <div style={SECTION_TITLE}>MFA failures (per account)</div>
        <div style={ROW}>
          <div>
            <label style={LABEL}>Threshold (failures)</label>
            <input type="number" min="1" max="50" value={config.mfa_threshold} onChange={e => setConfig({ ...config, mfa_threshold: parseInt(e.target.value) || 3 })} style={INPUT} />
          </div>
          <div>
            <label style={LABEL}>Window (minutes)</label>
            <input type="number" min="1" max="120" value={config.mfa_window_min} onChange={e => setConfig({ ...config, mfa_window_min: parseInt(e.target.value) || 10 })} style={INPUT} />
          </div>
        </div>
        <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>Default: 3 failures in 10 min.</div>
      </div>

      <div style={SECTION}>
        <div style={SECTION_TITLE}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={config.reset_after_failure_enabled} onChange={e => setConfig({ ...config, reset_after_failure_enabled: e.target.checked })} />
            Password reset after failed attempts
          </label>
        </div>
        <div>
          <label style={LABEL}>Minimum prior failures (in past 1h) to trigger</label>
          <input type="number" min="1" max="50" value={config.reset_after_failure_min_failures} onChange={e => setConfig({ ...config, reset_after_failure_min_failures: parseInt(e.target.value) || 3 })} style={{ ...INPUT, maxWidth: 200 }} />
        </div>
        <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 6 }}>Alerts when someone requests a password reset right after failed logins - possible takeover attempt.</div>
      </div>

      <div style={SECTION}>
        <div style={SECTION_TITLE}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={config.off_hours_enabled} onChange={e => setConfig({ ...config, off_hours_enabled: e.target.checked })} />
            Off-hours logins
          </label>
        </div>
        <div style={ROW}>
          <div>
            <label style={LABEL}>Start hour (ET, 24h)</label>
            <input type="number" min="0" max="23" value={config.off_hours_start_hour_et} onChange={e => setConfig({ ...config, off_hours_start_hour_et: parseInt(e.target.value) || 0 })} style={INPUT} />
          </div>
          <div>
            <label style={LABEL}>End hour (ET, 24h)</label>
            <input type="number" min="0" max="23" value={config.off_hours_end_hour_et} onChange={e => setConfig({ ...config, off_hours_end_hour_et: parseInt(e.target.value) || 0 })} style={INPUT} />
          </div>
        </div>
        <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>Default: 22:00 - 06:00 ET. If start &gt; end, window wraps midnight.</div>
      </div>

      <div style={SECTION}>
        <div style={SECTION_TITLE}>Cooldown</div>
        <div>
          <label style={LABEL}>Minimum minutes between same-alert-key emails</label>
          <input type="number" min="1" max="1440" value={config.cooldown_min} onChange={e => setConfig({ ...config, cooldown_min: parseInt(e.target.value) || 30 })} style={{ ...INPUT, maxWidth: 200 }} />
        </div>
        <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 6 }}>Default: 30 min. Prevents re-emailing the same IP/account repeatedly during a sustained event.</div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
        <button onClick={save} disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: saving ? 'var(--gray-300)' : 'var(--navy, #00395C)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: saving ? 'default' : 'pointer' }}>
          {saving ? <Loader size={14} className="spin" /> : <Save size={14} />}
          {saving ? 'Saving...' : 'Save changes'}
        </button>
        <button onClick={runTestScan} disabled={testRunning} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#fff', color: 'var(--navy, #00395C)', border: '1.5px solid var(--navy, #00395C)', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: testRunning ? 'default' : 'pointer' }}>
          {testRunning ? <Loader size={14} className="spin" /> : <Play size={14} />}
          {testRunning ? 'Scanning...' : 'Run scan now'}
        </button>
        {savedMsg && <div style={{ color: '#15803d', fontSize: 12, fontWeight: 600 }}>{savedMsg}</div>}
      </div>

      {testResult && (
        <div style={{ marginTop: 16, padding: 12, background: 'var(--gray-50, #f9fafb)', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 300, overflow: 'auto' }}>
          {JSON.stringify(testResult, null, 2)}
        </div>
      )}
    </div>
  );
}